import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import TaskSummary from '../../components/common/TaskSummary';

const ToDoLists = () => {
    const [tasks, setTasks] = useState([]);
    const [newTask, setNewTask] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('Low');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [taskSummary, setTaskSummary] = useState([]);

    // Monitor tasks state changes
    useEffect(() => {
        console.log('Tasks state updated:', tasks);
    }, [tasks]);

    useEffect(() => {
        const fetchTasks = async () => {
            try {
                console.log('Fetching tasks...');
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/todo`);
                if (response.ok) {
                    const data = await response.json();
                    console.log('Raw API response:', data);
                    
                    // Handle the MariaDB response format
                    let tasksArray = data;
                    if (!Array.isArray(data)) {
                        tasksArray = Object.values(data);
                    }
                    console.log('Tasks array:', tasksArray);
                    
                    // Convert dueDate strings to Date objects
                    const tasksWithDates = tasksArray.map(task => ({
                        ...task,
                        dueDate: new Date(task.due_date)
                    }));
                    console.log('Tasks with dates:', tasksWithDates);
                    
                    setTasks(tasksWithDates);
                } else {
                    console.error('Failed to fetch tasks:', response.statusText);
                }
            } catch (error) {
                console.error('Error fetching tasks:', error);
            }
        };

        fetchTasks();
    }, []);

    // Fetch task summary
    useEffect(() => {
        const fetchTaskSummary = async () => {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/todo/summary`);
                if (response.ok) {
                    const data = await response.json();
                    setTaskSummary(data);
                } else {
                    console.error('Failed to fetch task summary:', response.statusText);
                }
            } catch (error) {
                console.error('Error fetching task summary:', error);
            }
        };

        fetchTaskSummary();
    }, []);

    const addTask = async () => {
        if (newTask.trim() !== '') {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/todo`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        text: newTask,
                        priority: selectedPriority,
                        dueDate: selectedDate.toISOString().slice(0, 19).replace('T', ' '), // Format to 'YYYY-MM-DD HH:MM:SS'
                        status: 'Not Started'
                    })
                });

                if (response.ok) {
                    const savedTask = await response.json();
                    savedTask.dueDate = new Date(savedTask.due_date);
                    setTasks(prevTasks => [...prevTasks, savedTask]);
                    setNewTask('');
                    
                    // Refresh task summary
                    const summaryResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/todo/summary`);
                    if (summaryResponse.ok) {
                        const summaryData = await summaryResponse.json();
                        setTaskSummary(summaryData);
                    }
                } else {
                    console.error('Failed to save task:', response.statusText);
                }
            } catch (error) {
                console.error('Error adding task:', error);
            }
        }
    };

    const updateTaskStatus = async (taskId, newStatus) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/todo/${taskId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: newStatus })
            });

            if (response.ok) {
                setTasks(tasks.map(task => {
                    if (task.id === taskId) {
                        return { ...task, status: newStatus };
                    }
                    return task;
                }));

                // Refresh task summary
                const summaryResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/todo/summary`);
                if (summaryResponse.ok) {
                    const summaryData = await summaryResponse.json();
                    setTaskSummary(summaryData);
                }
            } else {
                console.error('Failed to update task status:', response.statusText);
            }
        } catch (error) {
            console.error('Error updating task status:', error);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Not Started':
                return 'bg-gray-100';
            case 'Started':
                return 'bg-yellow-100';
            case 'In-Progress':
                return 'bg-blue-100';
            case 'Complete':
                return 'bg-green-100';
            default:
                return 'bg-gray-100';
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'Low':
                return 'text-green-600';
            case 'Medium':
                return 'text-yellow-600';
            case 'High':
                return 'text-red-600';
            default:
                return 'text-gray-600';
        }
    };

    return (
        <div className="p-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">ToDo Lists</h1>
                <p className="text-gray-500">Manage your tasks and stay organized</p>
            </div>

            {/* Task Summary Section */}
            <TaskSummary className="mb-8" />

            <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                <div className="flex flex-col md:flex-row gap-4">
                    <input
                        type="text"
                        value={newTask}
                        onChange={(e) => setNewTask(e.target.value)}
                        placeholder="Enter new task"
                        className="flex-grow p-2 border rounded-md"
                    />
                    <select
                        value={selectedPriority}
                        onChange={(e) => setSelectedPriority(e.target.value)}
                        className="p-2 border rounded-md"
                    >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                    </select>
                    <DatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        className="p-2 border rounded-md"
                        dateFormat="yyyy-MM-dd"
                    />
                    <button
                        onClick={addTask}
                        className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
                    >
                        Add Task
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {tasks && tasks.length > 0 ? (
                    tasks.map((task) => (
                        <div
                            key={task.id}
                            className={`${getStatusColor(task.status)} p-4 rounded-lg border transition-all duration-200`}
                        >
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-semibold">{task.text}</h3>
                                    <div className="flex gap-4 mt-2 text-sm">
                                        <span className={`font-medium ${getPriorityColor(task.priority)}`}>
                                            {task.priority}
                                        </span>
                                        <span className="text-gray-600">
                                            Due: {task.dueDate.toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                                <select
                                    value={task.status}
                                    onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                                    className="p-2 border rounded-md bg-white"
                                >
                                    <option value="Not Started">Not Started</option>
                                    <option value="Started">Started</option>
                                    <option value="In-Progress">In-Progress</option>
                                    <option value="Complete">Complete</option>
                                </select>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-gray-500 text-center py-4">No tasks found</div>
                )}
            </div>
        </div>
    );
};

export default ToDoLists;